import { useState, useMemo } from 'react'
import { Loader2, Check } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useParty } from '@/hooks/useParty'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { useDistrictsWithRegionByNation } from '@/hooks/useDistrictsWithRegionByNation'
import {
  executePollInstantly,
  pollingSnapshotKeys,
} from '@/services/pollingSnapshots'
import { partyActionKeys } from '@/services/partyActions'
import { actionPointKeys } from '@/services/actionPoints'
import { partyKeys } from '@/services/parties'
import {
  POLL_SCOPES,
  POLL_DETAIL_LEVELS,
  POLL_SCOPE_ORDER,
  POLL_INSIGHTS,
  type PollScope,
  type PollDetailLevel,
} from '@/constants/polling'
import { cn } from '@/lib/utils'
import type { PollingSnapshot } from '@/types'

interface PollActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: (snapshot: PollingSnapshot) => void
  onBack?: () => void
}

export function PollActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: PollActionFormProps) {
  if (!open) return null

  return (
    <PollActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function PollActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: PollActionFormProps) {
  const queryClient = useQueryClient()

  // Form state
  const [scope, setScope] = useState<PollScope>('national')
  const [detailLevel, setDetailLevel] = useState<PollDetailLevel>('basic')
  const [selectedRegionId, setSelectedRegionId] = useState<string>('')
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('')

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Fetch party data to get nation_id
  const { party, isLoading: partyLoading } = useParty(partyId)

  // Fetch regions and districts for the nation
  const { regions, isLoading: regionsLoading } = useRegionsByNation(party?.nation_id)
  const { districts, isLoading: districtsLoading } = useDistrictsWithRegionByNation(party?.nation_id)

  // Calculate total AP cost
  const totalCost = useMemo(() => {
    return POLL_SCOPES[scope].baseCost + POLL_DETAIL_LEVELS[detailLevel].additionalCost
  }, [scope, detailLevel])

  // Get selected target info for display
  const selectedTarget = useMemo(() => {
    if (scope === 'national') return null
    if (scope === 'regional' && selectedRegionId) {
      return regions?.find((r) => r.id === selectedRegionId)
    }
    if (scope === 'district' && selectedDistrictId) {
      return districts?.find((d) => d.id === selectedDistrictId)
    }
    return null
  }, [scope, selectedRegionId, selectedDistrictId, regions, districts])

  // Validation
  const canSubmit = useMemo(() => {
    if (isExecuting) return false
    if (totalCost > availableAP) return false
    if (scope === 'regional' && !selectedRegionId) return false
    if (scope === 'district' && !selectedDistrictId) return false
    if (!party?.nation_id) return false
    return true
  }, [isExecuting, totalCost, availableAP, scope, selectedRegionId, selectedDistrictId, party?.nation_id])

  const apAfterAction = availableAP - totalCost

  // Reset target selections when scope changes
  const handleScopeChange = (newScope: PollScope) => {
    setScope(newScope)
    setSelectedRegionId('')
    setSelectedDistrictId('')
    setError(null)
  }

  const handleSubmit = async () => {
    if (!canSubmit || !party?.nation_id) return

    setIsExecuting(true)
    setError(null)

    let targetId: string | undefined
    let targetName: string | undefined

    if (scope === 'regional' && selectedRegionId) {
      targetId = selectedRegionId
      targetName = regions?.find((r) => r.id === selectedRegionId)?.name
    } else if (scope === 'district' && selectedDistrictId) {
      targetId = selectedDistrictId
      const district = districts?.find((d) => d.id === selectedDistrictId)
      targetName = district?.name
    }

    try {
      // Execute poll instantly
      const result = await executePollInstantly({
        partyId,
        nationId: party.nation_id,
        tick: currentTick,
        scope,
        detailLevel,
        targetId,
        targetName,
      })

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: pollingSnapshotKeys.byParty(partyId),
      })
      queryClient.invalidateQueries({
        queryKey: partyActionKeys.byParty(partyId),
      })
      queryClient.invalidateQueries({
        queryKey: actionPointKeys.byPartyTick(partyId, currentTick),
      })
      queryClient.invalidateQueries({
        queryKey: partyKeys.detail(partyId),
      })

      // Close the form and pass the snapshot to the callback
      onOpenChange(false)
      onSuccess?.(result.snapshot)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to execute poll'))
    } finally {
      setIsExecuting(false)
    }
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  const isLoading = partyLoading || regionsLoading || districtsLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>📊</span> Configure Polling
          </DialogTitle>
          <DialogDescription>
            Research voter preferences to identify battlegrounds and understand
            which issues matter most.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Scope Selection */}
          <div className="space-y-3">
            <Label>Scope</Label>
            <div className="grid grid-cols-3 gap-2">
              {POLL_SCOPE_ORDER.map((scopeKey) => {
                const scopeConfig = POLL_SCOPES[scopeKey]
                const isSelected = scope === scopeKey

                return (
                  <Button
                    key={scopeKey}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    className="flex flex-col h-auto py-3 px-2"
                    onClick={() => handleScopeChange(scopeKey)}
                  >
                    <span className="text-lg">{scopeConfig.icon}</span>
                    <span className="text-xs mt-1">{scopeConfig.label}</span>
                  </Button>
                )
              })}
            </div>
            <p className="text-muted-foreground text-xs">
              {POLL_SCOPES[scope].description}
            </p>
          </div>

          {/* Regional Selector */}
          {scope === 'regional' && (
            <div className="space-y-2">
              <Label htmlFor="region-select">Select Region</Label>
              {isLoading ? (
                <div className="flex h-9 items-center justify-center">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                </div>
              ) : (
                <Select
                  value={selectedRegionId}
                  onValueChange={setSelectedRegionId}
                >
                  <SelectTrigger id="region-select">
                    <SelectValue placeholder="Choose a region..." />
                  </SelectTrigger>
                  <SelectContent>
                    {regions?.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* District Selector */}
          {scope === 'district' && (
            <div className="space-y-2">
              <Label htmlFor="district-select">Select District</Label>
              {isLoading ? (
                <div className="flex h-9 items-center justify-center">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                </div>
              ) : (
                <Select
                  value={selectedDistrictId}
                  onValueChange={setSelectedDistrictId}
                >
                  <SelectTrigger id="district-select">
                    <SelectValue placeholder="Choose a district..." />
                  </SelectTrigger>
                  <SelectContent>
                    {districts?.map((district) => (
                      <SelectItem key={district.id} value={district.id}>
                        {district.name} ({district.region.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Detail Level Selection */}
          <div className="space-y-3">
            <Label>Detail Level</Label>
            <div className="grid grid-cols-2 gap-3">
              {(['basic', 'detailed'] as const).map((level) => {
                const levelConfig = POLL_DETAIL_LEVELS[level]
                const isSelected = detailLevel === level

                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setDetailLevel(level)}
                    className={cn(
                      'rounded-lg border-2 p-3 text-left transition-all',
                      'hover:border-primary/50 hover:bg-accent/50',
                      isSelected
                        ? 'border-primary bg-accent'
                        : 'border-border bg-background'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{levelConfig.label}</span>
                        {levelConfig.additionalCost > 0 && (
                          <span className="text-xs text-muted-foreground">
                            +{levelConfig.additionalCost} AP
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {levelConfig.description}
                    </p>
                    <div className="mt-2 text-xs text-muted-foreground">
                      ±{levelConfig.marginOfError[0]}-{levelConfig.marginOfError[1]}% margin
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* What You'll Learn */}
          <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
            <h4 className="text-sm font-medium">What You'll Learn</h4>
            <ul className="space-y-1 text-sm">
              {POLL_INSIGHTS[detailLevel].map((insight, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{insight}</span>
                </li>
              ))}
            </ul>
            {selectedTarget && (
              <p className="text-xs text-muted-foreground pt-2 border-t mt-3">
                Target: {selectedTarget.name}
                {scope === 'district' && 'population' in selectedTarget && (
                  <> (Pop: {selectedTarget.population.toLocaleString()})</>
                )}
              </p>
            )}
          </div>

          {/* AP Cost Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Cost:</span>
            <span className="font-medium">{totalCost} AP</span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span
              className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}
            >
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Error display */}
          {error && (
            <p className="text-destructive text-sm">
              {error.message || 'Failed to queue action'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isExecuting}
          >
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isExecuting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isExecuting ? 'Running Poll...' : 'Run Poll'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
